import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getToken, logout } from "../utils/auth";
import { 
  FiSearch, 
  FiUsers, 
  FiCheckSquare, 
  FiChevronLeft, 
  FiChevronRight,
  FiBarChart2,
  FiX,
  FiSend,
  FiBookOpen,
  FiCheckCircle,
  FiAward
} from "react-icons/fi"; 
import { Chart } from "chart.js/auto";
import swal from "sweetalert";
import "./ViewStudentsByBatch.css";

const ITEMS_PER_PAGE = 10;

export default function ViewStudentsByBatch() {
  const location = useLocation();
  const navigate = useNavigate();

  const college = location.state?.college || localStorage.getItem("selectedCampus");
  const year = location.state?.year || localStorage.getItem("selectedYear");
  const batch = location.state?.batch || localStorage.getItem("selectedBatch");

  const [students, setStudents] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [mcqs, setMcqs] = useState([]);
  const [selectedAssignment, setSelectedAssignment] = useState("");
  const [selectedMcq, setSelectedMcq] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  const [performanceStudent, setPerformanceStudent] = useState(null);
  const [performanceType, setPerformanceType] = useState("assignment");
  const [assignmentPerformance, setAssignmentPerformance] = useState([]);
  const [mcqPerformance, setMcqPerformance] = useState([]);
  const [performanceLoading, setPerformanceLoading] = useState(false);
  const [showPerformanceChart, setShowPerformanceChart] = useState(false);

  const performanceChartRef = useRef(null);
  const performanceChartInstance = useRef(null);

  useEffect(() => {
    if (!college || !year || !batch) navigate("/campuses");
  }, [college, year, batch, navigate]);

  useEffect(() => {
    async function fetchInitialData() {
      setLoading(true);
      try {
        const [sRes, aRes, mRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/students/students?college=${college}&year=${year}&batch=${batch}`, {
            headers: { Authorization: `Bearer ${getToken()}` }
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/assignments`, {
            headers: { Authorization: `Bearer ${getToken()}` }
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/mcqs/getallmcq`, { 
            method: "POST", 
            headers: { Authorization: `Bearer ${getToken()}` } 
          })
        ]);

        if (sRes.status === 401 || sRes.status === 403) {
          logout();
          navigate("/login");
          return;
        }

        const sData = await sRes.json();
        const aData = await aRes.json();
        const mData = await mRes.json();

        setStudents(sData || []);
        setAssignments(aData || []);
        setMcqs(mData || []);
      } catch (err) {
        console.error("Fetch Error:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchInitialData();
  }, [college, year, batch, navigate]);

  const filteredStudents = useMemo(() => {
    return students.filter((s) =>
      `${s.name} ${s.email} ${s.regNo}`.toLowerCase().includes(search.toLowerCase())
    );
  }, [students, search]);

  useEffect(() => setPage(1), [search]);

  const totalPages = Math.max(1, Math.ceil(filteredStudents.length / ITEMS_PER_PAGE));
  const paginatedStudents = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredStudents.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredStudents, page]);

  const toggleStudent = (email) => {
    setSelectedStudents(prev => 
      prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]
    );
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const currentIds = paginatedStudents.map(s => s.email);
      setSelectedStudents(prev => [...new Set([...prev, ...currentIds])]);
    } else {
      const currentIds = paginatedStudents.map(s => s.email);
      setSelectedStudents(prev => prev.filter(email => !currentIds.includes(email)));
    }
  };

  const handleBulkAction = async (type) => {
    const id = type === 'assignment' ? selectedAssignment : selectedMcq;

    if (!id || selectedStudents.length === 0) {
      swal("Missing Selection", "Please select students and an item to assign.", "warning");
      return;
    }

    setIsProcessing(true);

    try {
      const endpoint = type === 'assignment' ? '/addAssignmentToUser' : '/addMcq';
      const assignedAt = new Date().toISOString();

      const bodyGenerator = (email) =>
        type === 'assignment'
          ? { email, assignmentId: id, assignedAt }
          : { email, mcq: mcqs.find(m => m._id === id), assignedAt };

      await Promise.all(
        selectedStudents.map(email =>
          fetch(`${process.env.REACT_APP_API_BASE_URL}${endpoint}`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
            body: JSON.stringify(bodyGenerator(email))
          })
        )
      );

      swal("Assigned Successfully", `Item assigned to ${selectedStudents.length} student(s).`, "success");
      setSelectedStudents([]);
      setSelectedAssignment("");
      setSelectedMcq("");
    } catch (err) {
      console.error(err);
      swal("Action Failed", "An error occurred while processing assignments.", "error");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleViewPerformance = async (student, type = "assignment") => {
    setPerformanceStudent(student);
    setPerformanceType(type);
    setAssignmentPerformance([]);
    setMcqPerformance([]);
    setShowPerformanceChart(false);
    setPerformanceLoading(true);

    try {
      const url = type === "assignment"
        ? `${process.env.REACT_APP_API_BASE_URL}/api/assignment-submissions/student/${student._id}`
        : `${process.env.REACT_APP_API_BASE_URL}/api/mcq-submissions/student/${student._id}`;

      const response = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!response.ok) throw new Error("Failed to fetch performance data");

      const data = await response.json();
      const results = Array.isArray(data) ? data : data.submissions || [];
      
      const normalized = results.map((item) => {
        if (type === "assignment") {
          return {
            ...item,
            percentage: item.percentage ?? (item.numberOfProblems
              ? Math.round((item.problemsSolved / item.numberOfProblems) * 100)
              : 0),
          };
        }
        return {
          ...item,
          percentage: item.percentage ?? (item.totalMarks
            ? Math.round((item.score / item.totalMarks) * 100)
            : 0),
        };
      });

      if (type === "assignment") {
        setAssignmentPerformance(normalized);
      } else {
        setMcqPerformance(normalized);
      }
    } catch (err) {
      console.error("Performance fetch error:", err);
      swal("Error", "Unable to load performance data.", "error");
      setPerformanceStudent(null);
    } finally {
      setPerformanceLoading(false);
    }
  };

  useEffect(() => {
    const currentPerformance = performanceType === "assignment" ? assignmentPerformance : mcqPerformance;
    if (!showPerformanceChart || !currentPerformance.length) return;

    performanceChartInstance.current?.destroy();
    performanceChartInstance.current = new Chart(performanceChartRef.current, {
      type: "line",
      data: {
        labels: currentPerformance.map((submission) =>
          performanceType === "assignment"
            ? submission.assignmentName
            : submission.mcqName || submission.mcq?.topic || `MCQ`
        ),
        datasets: [
          {
            label: "Score (%)",
            data: currentPerformance.map((submission) => submission.percentage || 0),
            borderColor: "#4f46e5",
            backgroundColor: "rgba(79, 70, 229, 0.08)",
            pointBackgroundColor: "#4f46e5",
            pointBorderColor: "#ffffff",
            pointBorderWidth: 2,
            pointRadius: 5,
            pointHoverRadius: 7,
            tension: 0.35,
            fill: true
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true,
            max: 100,
            ticks: { callback: (value) => `${value}%`, font: { family: 'Inter, sans-serif' } },
            grid: { color: "rgba(226, 232, 240, 0.6)" }
          },
          x: {
            grid: { display: false },
            ticks: { font: { family: 'Inter, sans-serif' } }
          }
        },
        plugins: {
          tooltip: { 
            padding: 10,
            cornerRadius: 8,
            callbacks: { label: (context) => `Score: ${context.raw}%` } 
          },
          legend: { display: false }
        }
      }
    });

    return () => performanceChartInstance.current?.destroy();
  }, [showPerformanceChart, assignmentPerformance, mcqPerformance, performanceType]);

  const closePerformanceModal = () => {
    setPerformanceStudent(null);
    setShowPerformanceChart(false);
  };

  if (loading) return (
    <div className="vsb-loader-wrapper">
      <div className="vsb-spinner"></div>
      <p className="vsb-loading-text">Loading Student Directory...</p>
    </div>
  );

  const currentPerformance = performanceType === "assignment" ? assignmentPerformance : mcqPerformance;

  return (
    <div className="vsb-page-container">
      {/* HEADER SECTION */}
      <header className="vsb-header">
        <div className="vsb-header-title">
          <h1>{batch} Dashboard</h1>
          <p className="vsb-subtitle">{college} <span className="vsb-dot">•</span> Academic Year {year}</p>
        </div>
        <div className="vsb-search-box">
          <FiSearch className="vsb-search-icon" />
          <input 
            type="text" 
            placeholder="Search by name, email, or registration no..." 
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </header>

      {/* STATS OVERVIEW */}
      <div className="vsb-stats-grid">
        <div className="vsb-stat-card">
          <div className="vsb-stat-icon icon-indigo"><FiUsers /></div>
          <div className="vsb-stat-info">
            <span className="vsb-stat-label">Total Batch Cohort</span>
            <h3 className="vsb-stat-value">{students.length}</h3>
          </div>
        </div>
        <div className="vsb-stat-card">
          <div className="vsb-stat-icon icon-emerald"><FiCheckSquare /></div>
          <div className="vsb-stat-info">
            <span className="vsb-stat-label">Selected Students</span>
            <h3 className="vsb-stat-value">{selectedStudents.length}</h3>
          </div>
        </div>
      </div>

      {/* FLOATING BULK ACTIONS BAR */}
      <div className={`vsb-action-bar ${selectedStudents.length > 0 ? 'visible' : ''}`}>
        <div className="vsb-action-badge">
          <FiCheckCircle className="vsb-badge-icon" />
          <span><b>{selectedStudents.length}</b> Students Selected</span>
        </div>
        
        <div className="vsb-action-controls">
          <div className="vsb-control-group">
            <select value={selectedAssignment} onChange={(e) => setSelectedAssignment(e.target.value)}>
              <option value="">Select Assignment</option>
              {assignments.map(a => <option key={a._id} value={a._id}>{a.name}</option>)}
            </select>
            <button className="vsb-btn vsb-btn-primary" onClick={() => handleBulkAction('assignment')} disabled={isProcessing}>
              <FiSend /> Assign
            </button>
          </div>

          <div className="vsb-separator"></div>

          <div className="vsb-control-group">
            <select value={selectedMcq} onChange={(e) => setSelectedMcq(e.target.value)}>
              <option value="">Select MCQ Test</option>
              {mcqs.map(m => <option key={m._id} value={m._id}>{m.category} - {m.topic}</option>)}
            </select>
            <button className="vsb-btn vsb-btn-secondary" onClick={() => handleBulkAction('mcq')} disabled={isProcessing}>
              <FiSend /> Assign MCQ
            </button>
          </div>
        </div>
      </div>

      {/* MODERN DATA TABLE */}
      <div className="vsb-table-card">
        <table className="vsb-table">
          <thead>
            <tr>
              <th className="vsb-th-checkbox">
                <input 
                  type="checkbox" 
                  className="vsb-checkbox"
                  onChange={handleSelectAll}
                  checked={paginatedStudents.length > 0 && paginatedStudents.every(s => selectedStudents.includes(s.email))}
                />
              </th>
              <th>Student Details</th>
              <th>Reg. Number</th>
              <th>Contact Number</th>
              <th className="text-right">Performance Metrics</th>
            </tr>
          </thead>
          <tbody>
            {paginatedStudents.map((s) => {
              const isSelected = selectedStudents.includes(s.email);
              return (
                <tr key={s._id} className={isSelected ? 'row-active' : ''}>
                  <td className="vsb-th-checkbox">
                    <input
                      type="checkbox"
                      className="vsb-checkbox"
                      checked={isSelected}
                      onChange={() => toggleStudent(s.email)}
                    />
                  </td>
                  <td>
                    <div className="vsb-profile">
                      <div className="vsb-avatar">{s?.name?.charAt(0).toUpperCase()}</div>
                      <div className="vsb-profile-info">
                        <span className="vsb-name">{s.name}</span>
                        <span className="vsb-email">{s.email}</span>
                      </div>
                    </div>
                  </td>
                  <td><span className="vsb-reg-badge">{s.regNo}</span></td>
                  <td><span className="vsb-phone">{s.phNo || "—"}</span></td>
                  <td className="text-right">
                    <div className="vsb-inline-actions">
                      <button className="vsb-btn-pill" onClick={() => handleViewPerformance(s, "assignment")}>
                        <FiBookOpen /> Assignments
                      </button>
                      <button className="vsb-btn-pill" onClick={() => handleViewPerformance(s, "mcq")}>
                        <FiAward /> MCQs
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        
        {filteredStudents.length === 0 && (
          <div className="vsb-empty-state">
            <FiSearch className="vsb-empty-icon" />
            <p className="vsb-empty-title">No matching students found</p>
            <p className="vsb-empty-subtitle">Try adjusting your search criteria.</p>
          </div>
        )}
      </div>

      {/* FOOTER PAGINATION */}
      <footer className="vsb-footer">
        <p className="vsb-footer-info">
          Showing <b>{paginatedStudents.length}</b> of <b>{filteredStudents.length}</b> students
        </p>
        <div className="vsb-pagination">
          <button className="vsb-page-btn" disabled={page === 1} onClick={() => setPage(page - 1)}>
            <FiChevronLeft /> Prev
          </button>
          <span className="vsb-page-indicator">Page {page} of {totalPages}</span>
          <button className="vsb-page-btn" disabled={page === totalPages} onClick={() => setPage(page + 1)}>
            Next <FiChevronRight />
          </button>
        </div>
      </footer>

      {/* PERFORMANCE MODAL */}
      {performanceStudent && (
        <div className="vsb-modal-overlay" onClick={closePerformanceModal}>
          <div className="vsb-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="vsb-modal-header">
              <div>
                <h2>{performanceType === "assignment" ? "Assignment Analytics" : "MCQ Analytics"}</h2>
                <p className="vsb-modal-subtitle">{performanceStudent.name} <span className="vsb-dot">•</span> {performanceStudent.email}</p>
              </div>
              <button className="vsb-modal-close" onClick={closePerformanceModal}>
                <FiX />
              </button>
            </div>

            <div className="vsb-modal-body">
              {performanceLoading ? (
                <div className="vsb-modal-loader">
                  <div className="vsb-spinner small"></div>
                  <p>Fetching performance metrics...</p>
                </div>
              ) : currentPerformance.length === 0 ? (
                <div className="vsb-modal-loader">
                  <p>No submission logs recorded for this student.</p>
                </div>
              ) : (
                <>
                  <div className="vsb-modal-actions">
                    <button
                      className="vsb-btn vsb-btn-chart"
                      onClick={() => setShowPerformanceChart((visible) => !visible)}
                    >
                      <FiBarChart2 /> {showPerformanceChart ? "Hide Chart" : "View Trend Chart"}
                    </button>
                  </div>

                  {showPerformanceChart && (
                    <div className="vsb-chart-wrapper">
                      <canvas ref={performanceChartRef}></canvas>
                    </div>
                  )}

                  <div className="vsb-record-list">
                    {currentPerformance.map((submission, index) => (
                      <div className="vsb-record-item" key={submission._id || index}>
                        <div className="vsb-record-meta">
                          <strong>
                            {performanceType === "assignment" 
                              ? (submission.assignmentName || `Assignment ${index + 1}`)
                              : (submission.mcqName || submission.mcq?.topic || `MCQ Test ${index + 1}`)}
                          </strong>
                          <span>
                            {performanceType === "assignment"
                              ? `${submission.problemsSolved || 0} of ${submission.numberOfProblems || 0} Solved`
                              : `Score: ${submission.score || 0} / ${submission.totalMarks || 0}`}
                          </span>
                        </div>
                        <div className={`vsb-score-pill ${submission.percentage >= 70 ? 'high' : submission.percentage >= 40 ? 'mid' : 'low'}`}>
                          {submission.percentage}%
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}